import {
    FormControlLabel, Grid2, Switch, Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import cx from "classnames";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import React, { useState } from "react";
import { useBlocker } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";
import { Controller } from "react-hook-form";
import { useTranslate } from "../../../../../hooks/useTranslate";
import TextInput from "../../../../../components/TextInput";
import { useContactForm } from "../../hooks/useContactForm";
import Select from "../../../../../components/Select";
import ResponsibleForField from "../ResponsibleForField";
import IconStripe from "../../../../../assets/images/icons/stripe-icon.svg?react";
import ImgStripeIdentity from "../../../../../assets/images/images/stripe-identity.png";
import DatePickerInput from "../../../../../components/DatePickerInput";
import { CONTACT_MAX_DATE_OF_BIRTH } from "../../constants";
import Preloader from "../../../../../components/Preloader";
import BaseDialog from "../../../../../components/BaseDialog";
import PhoneNumberInput from "../../../../../components/PhoneNumberInput";
import ContactTypeField from "../ContactTypeField";
import FileDropUploader from "../../../../../components/FileDropUploader";
import useVerificationStatus from "../../../../../context/status/useStatuses";
import {
    KEY_APPROVED,
    KEY_NONE,
} from "../../../../../utils/getVerificationStatusKey";
import VerificationStatusChip from "../../../../../components/VerificationStatusChip";
import {
    isUKDevelopment,
    isUSDevelopment,
    isLocal,
} from "../../../../../services/serviceConfig";
import { isVersionUS } from "../../../../../services/versionService";
import axiosManager from "../../../../../services/axiosManager";
import SnackbarToast from "../../../../../components/SnackbarToast";
import { titles } from "../../../../../constants/titles";
import "./ContactInformationForm.css";
import CountrySelect from "../../../../../components/CountrySelect";
import { useStudents } from "../../../../../context/students";
import { STRIPE_KEY_DEV, STRIPE_KEY_PROD } from "../../../../../services/stripeConfig";

const titlesOptions = titles.map((value) => ({ value, label: value }));

const ContactInformationForm = (props) => {
    const {
        data,
        relationshipsOptions,
        isLoadingRelations,
        isDisabled,
        isPrimaryContact,
        isSaving,
        isShortForm,
        onCancel,
        onSave,
        saveButtonText,
        hideTitle,
        showHubAccess,
    } = props;

    const [isStripeVerification, setIsStripeVerification] = useState(true);
    const [stripeVerificationSuccess, setStripeVerificationSuccess] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [proofOfId, setProofOfId] = useState(null);

    const { t } = useTranslate();
    const { students } = useStudents();
    const {
        primaryContactVerificationKey,
        refetchPrimaryContactVerificationStatus,
    } = useVerificationStatus();
    const {
        register, control, errors, isDirtyForm, handleSubmit, watch,
    } = useContactForm(data);

    const hasParentHubAccess = watch("hasParentHubAccess");
    const blocker = useBlocker(isDirtyForm);

    const onSubmit = handleSubmit((formData) => {
        onSave?.({
            isDirtyForm,
            ...formData,
            contactType: data?.contactType || formData?.contactType,
            contactNumberCountryCode:
                formData.contactNumberCountryCode.id.replace(/-\d+/g, ""),
            proofOfId: isStripeVerification ? null : proofOfId,
            studentsResponsibleFor: (students || []).map((student) => student.studentId),
        });
    });

    const onCancelButtonClick = () => {
        if (isDirtyForm) setShowCancelDialog(true);
        else onCancel();
    };
    const onCancelDialogClose = () => {
        blocker.reset?.();
        setShowCancelDialog(false);
    };
    const onCancelDialogContinue = () => {
        blocker.proceed?.();
        onCancel();
    };
    const onStripeManualSwitch = () => {
        setIsStripeVerification((prev) => !prev);
    };
    const onChangeProofOfId = (files) => {
        setProofOfId(files?.[0]);
    };

    const [isStripeError, setShowStripeError] = useState(false);

    const {
        mutateAsync: createVerificationSession,
        reset: resetIdentityCreating,
        isPending: isIdentityCreating,
    } = useMutation({
        mutationFn: () => axiosManager
            .post$("/parent/api/Identity/CreateVerificationSession")
            .then((response) => response?.data),
    });
    const onStripeClick = async () => {
        try {
            resetIdentityCreating();
            const data = await createVerificationSession();
            const stripe = await loadStripe(
                isUKDevelopment || isUSDevelopment || isLocal
                    ? STRIPE_KEY_DEV
                    : STRIPE_KEY_PROD,
            );

            const result = await stripe.verifyIdentity(data.clientSecret);
            if (result.error === null) setStripeVerificationSuccess(true);
            else setShowStripeError(true);
        } catch (err) {
            setShowStripeError(true);
        } finally {
            refetchPrimaryContactVerificationStatus();
        }
    };

    return (
        <Preloader isLoading={isSaving} overflow showInitiallyOverflow>
            <form className="contact-information-form" onSubmit={onSubmit}>
                {(!isShortForm && !hideTitle) && (
                    <Typography
                        variant="h4"
                        component="div"
                        className="text-primary contact-information-form__title"
                    >
                        {t("responsibleAdults.contactInformation.name")}
                    </Typography>
                )}
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 2 }} className="mb-4">
                                <Select
                                    label={t(
                                        "responsibleAdults.contactInformation.title",
                                    )}
                                    control={control}
                                    name="title"
                                    options={titlesOptions}
                                    error={errors.title?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 5 }} className="mb-4">
                                <TextInput
                                    label={t(
                                        "responsibleAdults.contactInformation.forename",
                                    )}
                                    register={register}
                                    name="forename"
                                    error={errors.forename?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 5 }} className="mb-4">
                                <TextInput
                                    label={t(
                                        "responsibleAdults.contactInformation.surname",
                                    )}
                                    register={register}
                                    name="surname"
                                    error={errors.surname?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            {isVersionUS && (
                                <Grid2 size={{ xs: 12, md: 2 }} className="mb-4">
                                    <TextInput
                                        label={t(
                                            "responsibleAdults.contactInformation.suffix",
                                        )}
                                        register={register}
                                        name="suffix"
                                        error={errors.suffix?.message}
                                        disabled={isDisabled}
                                    />
                                </Grid2>
                            )}
                            <Grid2 size={{ xs: 12, md: isVersionUS ? 5 : 6 }} className="mb-4">
                                <Select
                                    label={t(
                                        "responsibleAdults.contactInformation.relation",
                                    )}
                                    control={control}
                                    name="relationshipTypeId"
                                    disabled={isDisabled || isLoadingRelations}
                                    options={relationshipsOptions}
                                    error={errors.relationshipTypeId?.message}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: isVersionUS ? 5 : 6 }} className="mb-4">
                                <DatePickerInput
                                    label={t(
                                        "responsibleAdults.contactInformation.dateOfBirth",
                                    )}
                                    control={control}
                                    name="dateOfBirth"
                                    maxDate={CONTACT_MAX_DATE_OF_BIRTH}
                                    error={errors.dateOfBirth?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 size={12} className="mb-4">
                            <PhoneNumberInput
                                label={t(
                                    "responsibleAdults.contactInformation.contactNumber",
                                )}
                                control={control}
                                register={register}
                                name="contactNumber"
                                error={errors.contactNumber?.message}
                                nameCountryCode="contactNumberCountryCode"
                                errorCountryCode={
                                    errors.contactNumberCountryCode?.message
                                }
                                disabled={isDisabled}
                            />
                        </Grid2>
                        <Grid2 size={12} className="mb-4">
                            <TextInput
                                label={t(
                                    "responsibleAdults.contactInformation.email",
                                )}
                                register={register}
                                name="emailAddress"
                                error={errors.emailAddress?.message}
                                disabled={isPrimaryContact || isDisabled}
                            />
                        </Grid2>
                        <Grid2 size={12} className="mb-4">
                            <TextInput
                                label={t(
                                    "responsibleAdults.contactInformation.emailConfirm",
                                )}
                                register={register}
                                name="emailAddressConfirm"
                                error={errors.emailAddressConfirm?.message}
                                disabled={isPrimaryContact || isDisabled}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Grid2 size={12} className="mb-4">
                            <TextInput
                                label={t(
                                    "responsibleAdults.contactInformation.addressLine1",
                                )}
                                register={register}
                                name="addressLine1"
                                error={errors.addressLine1?.message}
                                disabled={isDisabled}
                            />
                        </Grid2>
                        <Grid2 size={12} className="mb-4">
                            <TextInput
                                label={t(
                                    "responsibleAdults.contactInformation.addressLine2",
                                )}
                                register={register}
                                name="addressLine2"
                                error={errors.addressLine2?.message}
                                disabled={isDisabled}
                            />
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t(
                                        "responsibleAdults.contactInformation.city",
                                    )}
                                    register={register}
                                    name="city"
                                    error={errors.city?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t(
                                        "responsibleAdults.contactInformation.zip",
                                    )}
                                    register={register}
                                    name="code"
                                    error={errors.code?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t(
                                        "responsibleAdults.contactInformation.state",
                                    )}
                                    register={register}
                                    name="area"
                                    error={errors.area?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <CountrySelect
                                    label={t(
                                        "responsibleAdults.contactInformation.countryOfResidence",
                                    )}
                                    control={control}
                                    name="country"
                                    error={errors.country?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        {(!isPrimaryContact && showHubAccess) && (
                            <Grid2 size={12}>
                                <div className="label">
                                    {t("responsibleAdults.contactInformation.hasAccessTo")}
                                </div>
                                <Controller
                                    name="hasParentHubAccess"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FormControlLabel
                                            control={(
                                                <Switch
                                                    checked={value}
                                                    onChange={(_, checked) => {
                                                        onChange(checked);
                                                    }}
                                                />
                                            )}
                                            className="contact-information-form__toggle"
                                            label={t("myDetails.parentHub")}
                                            labelPlacement="start"
                                        />
                                    )}
                                />
                                <div className="label mt-4">
                                    {t("responsibleAdults.contactInformation.accessToggleTo")}
                                </div>
                                <div className="flex flex-column align-start">
                                    {/* <Controller */}
                                    {/*    name="allowFinance" */}
                                    {/*    control={control} */}
                                    {/*    render={({ field: { onChange, value } }) => ( */}
                                    {/*        <FormControlLabel */}
                                    {/*            disabled={!hasParentHubAccess} */}
                                    {/*            control={( */}
                                    {/*                <Switch */}
                                    {/*                    checked={value} */}
                                    {/*                    onChange={(_, checked) => { */}
                                    {/*                        onChange(checked); */}
                                    {/*                    }} */}
                                    {/*                /> */}
                                    {/*            )} */}
                                    {/*            className="contact-information-form__toggle" */}
                                    {/*            label={t("myDetails.finance")} */}
                                    {/*            labelPlacement="start" */}
                                    {/*        /> */}
                                    {/*    )} */}
                                    {/* /> */}
                                    <Controller
                                        name="allowRenewals"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <FormControlLabel
                                                disabled={!hasParentHubAccess}
                                                control={(
                                                    <Switch
                                                        checked={value}
                                                        onChange={(_, checked) => {
                                                            onChange(checked);
                                                        }}
                                                    />
                                                )}
                                                className="contact-information-form__toggle"
                                                label={t("myDetails.renewals")}
                                                labelPlacement="start"
                                            />
                                        )}
                                    />
                                </div>
                            </Grid2>
                        )}
                    </Grid2>
                </Grid2>
                {(isShortForm && !isPrimaryContact) && (
                    <Grid2 size={{ xs: 12 }} className="mb-8 mt-4">
                        <ContactTypeField
                            control={control}
                            name="contactType"
                        />
                    </Grid2>
                )}
                <Grid2 size={{ xs: 12 }} className="mb-4 mt-4" />
                {!isShortForm && isPrimaryContact
                    && primaryContactVerificationKey.key !== KEY_APPROVED && (
                    <>
                        <div className="divider mt-8 mb-8" />
                        <div className="flex xs:flex-column align-center justify-between gap-4">
                            <div>
                                <Typography
                                    variant="h4"
                                    component="div"
                                    className="text-primary"
                                >
                                    {t(
                                        "responsibleAdults.contactInformation.proofOfIdentity",
                                    )}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    component="div"
                                    className=""
                                >
                                    {t(
                                        "responsibleAdults.contactInformation.proofOfIdentitySubtitle",
                                    )}
                                </Typography>
                            </div>
                            {primaryContactVerificationKey.key !== KEY_NONE && (
                                <VerificationStatusChip
                                    status={primaryContactVerificationKey}
                                />
                            )}
                        </div>
                        {isStripeVerification ? (
                            <button
                                type="button"
                                className={cx("stripe-button mt-8 mb-8", {
                                    "stripe-button__success":
                                        stripeVerificationSuccess,
                                })}
                                onClick={onStripeClick}
                                disabled={isIdentityCreating}
                                // disabled={primaryContactVerificationKey.isInfo || primaryContactVerificationKey.isSuccess}
                            >
                                <IconStripe />
                                <div className="stripe-button__content">
                                    <div className="flex align-center gap-12">
                                        <Typography
                                            variant="h4"
                                            component="div"
                                            className="text-primary"
                                        >
                                            {t(
                                                "responsibleAdults.contactInformation.verifyMyId",
                                            )}
                                        </Typography>
                                        <img
                                            src={ImgStripeIdentity}
                                            alt="Stripe Identity"
                                            className="stripe-button__image"
                                        />
                                    </div>
                                    <Typography
                                        variant="body1"
                                        component="div"
                                        className="mb-8"
                                    >
                                        {t(
                                            "responsibleAdults.contactInformation.verifyMyIdSubtitle",
                                        )}
                                    </Typography>
                                </div>
                                <span className="stripe-button__arrow">
                                    <ArrowForwardIosOutlinedIcon />
                                </span>
                            </button>
                        ) : (
                            <FileDropUploader
                                className="mt-8 mb-8"
                                uploadTextElement={(
                                    <>
                                        <Typography
                                            variant="h4"
                                            component="div"
                                            className="text-primary"
                                        >
                                            {t(
                                                "responsibleAdults.contactInformation.pleaseAttachDocument",
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            component="div"
                                            className="mt-4"
                                        >
                                            {t(
                                                "responsibleAdults.contactInformation.documentsAccepts",
                                            )}
                                        </Typography>
                                    </>
                                )}
                                accept={{
                                    "image/jpeg": [".png", ".jpg", ".jpeg"],
                                    "application/pdf": [".pdf"],
                                    "application/word": [
                                        ".doc",
                                        ".docx",
                                        ".odt",
                                        ".rtf",
                                        ".txt",
                                    ],
                                }}
                                showFiles
                                onChange={onChangeProofOfId}
                                disabled={
                                    primaryContactVerificationKey.isInfo
                                        || primaryContactVerificationKey.isSuccess
                                }
                            />
                        )}
                        <button
                            type="button"
                            className="contact-information-form__manual-button mb-8"
                            onClick={onStripeManualSwitch}
                            disabled={stripeVerificationSuccess}
                        >
                            {isStripeVerification
                                ? t(
                                    "responsibleAdults.contactInformation.verifyIdentityManually",
                                )
                                : t(
                                    "responsibleAdults.contactInformation.verifyStripeIdentity",
                                )}
                        </button>
                    </>
                )}
                <div className="flex align-center gap-8">
                    <Button
                        variant="outlined"
                        className="contact-information-form__button"
                        onClick={onCancelButtonClick}
                        disabled={isIdentityCreating || isSaving}
                    >
                        {t("button.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent contact-information-form__button"
                        type="submit"
                        disabled={isDisabled || isIdentityCreating}
                        loading={isSaving}
                    >
                        {saveButtonText || t("button.save")}
                    </Button>
                </div>
            </form>
            <SnackbarToast
                open={isStripeError}
                isError
                onClose={resetIdentityCreating}
            >
                {t("responsibleAdults.contactInformation.errorStripeMessage")}
            </SnackbarToast>
            <BaseDialog
                open={showCancelDialog || blocker.state === "blocked"}
                onClose={onCancelDialogClose}
                title={t(
                    "responsibleAdults.contactInformation.cancelDialogTitle",
                )}
                withCloseButton
            >
                <div className="flex align-center gap-8">
                    <Button
                        variant="outlined"
                        className="contact-information-form__button"
                        onClick={onCancelDialogClose}
                    >
                        {t("button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent contact-information-form__button"
                        onClick={onCancelDialogContinue}
                    >
                        {t("button.continue")}
                    </Button>
                </div>
            </BaseDialog>
        </Preloader>
    );
};

export default ContactInformationForm;
